import { VERSION } from './constants.js';
import {
  fetchConsentConfiguration,
} from './config-fetch.js';
import type {
  ConsentConfigFetchErrorCode,
  ConsentConfigFetchResult,
} from './config-fetch.js';
import { createConsentId } from './consent-id.js';
import { createConsentEvent, isCanonicalLocale } from './consent-event.js';
import {
  createConsentEventDispatcher,
  validateConsentEventUrl,
} from './event-delivery.js';
import type { ConsentEventDispatcher } from './event-delivery.js';
import { createConsentEventQueue } from './event-queue.js';
import { evaluateConsentRequirement } from './consent-requirement.js';
import {
  ConsentCookieStorageError,
  createConsentCookieStorage,
} from './cookie-storage.js';
import type { ConsentCookieStorage } from './cookie-storage.js';
import {
  acceptAll as transitionAcceptAll,
  rejectAll as transitionRejectAll,
  savePreferences as transitionSavePreferences,
} from './state.js';
import type {
  ConsentBlockedReason,
  ConsentClient,
  ConsentClientDependencies,
  ConsentClientSnapshot,
  ConsentConfiguration,
  ConsentEventMap,
  ConsentEventType,
  ConsentInitOptions,
  ConsentInitResult,
  ConsentPreferenceInput,
  ConsentState,
  ConsentTransitionContext,
} from './types.js';

export type ConsentClientErrorCode =
  | 'action_not_allowed'
  | 'already_initialized'
  | 'initialization_options_invalid'
  | 'retry_not_available'
  | 'storage_unavailable'
  | 'storage_write_failed';

export class ConsentClientError extends Error {
  readonly code: ConsentClientErrorCode;
  readonly cause?: unknown;

  constructor(
    code: ConsentClientErrorCode,
    message: string,
    options: { readonly cause?: unknown } = {},
  ) {
    super(message);
    this.name = 'ConsentClientError';
    this.code = code;
    if (options.cause !== undefined) {
      this.cause = options.cause;
    }
  }
}

const IDLE_SNAPSHOT = Object.freeze({ status: 'idle' as const });
const INITIALIZING_SNAPSHOT = Object.freeze({
  status: 'initializing' as const,
});

function sameInitOptions(
  left: ConsentInitOptions,
  right: ConsentInitOptions,
): boolean {
  return (
    left.siteId === right.siteId &&
    left.configUrl === right.configUrl &&
    left.eventUrl === right.eventUrl &&
    left.cookieName === right.cookieName &&
    left.configTimeoutMs === right.configTimeoutMs &&
    left.eventTimeoutMs === right.eventTimeoutMs &&
    left.locale === right.locale &&
    left.allowInsecureLocalhost === right.allowInsecureLocalhost &&
    left.secureCookie === right.secureCookie
  );
}

function programmerConfigError(code: ConsentConfigFetchErrorCode): boolean {
  return (
    code === 'invalid_options' ||
    code === 'invalid_url' ||
    code === 'insecure_url'
  );
}

const SITE_ID_PATTERN = /^site_[a-z0-9][a-z0-9_-]{0,63}$/;

function validateInitOptions(options: ConsentInitOptions): void {
  if (!SITE_ID_PATTERN.test(options.siteId)) {
    throw new ConsentClientError(
      'initialization_options_invalid',
      'Site ID must use the canonical site_ format.',
    );
  }
  if (
    options.eventTimeoutMs !== undefined &&
    (!Number.isSafeInteger(options.eventTimeoutMs) ||
      options.eventTimeoutMs < 1 ||
      options.eventTimeoutMs > 60_000)
  ) {
    throw new ConsentClientError(
      'initialization_options_invalid',
      'Event timeout must be an integer between 1 and 60000 milliseconds.',
    );
  }
  if (options.locale !== undefined && !isCanonicalLocale(options.locale)) {
    throw new ConsentClientError(
      'initialization_options_invalid',
      'Locale must use a canonical BCP 47-like language tag.',
    );
  }
  if (options.eventUrl !== undefined) {
    try {
      validateConsentEventUrl(
        options.eventUrl,
        options.allowInsecureLocalhost ?? false,
      );
    } catch (error) {
      throw new ConsentClientError(
        'initialization_options_invalid',
        'Event URL must be absolute, credential-free and HTTPS except on explicit localhost development.',
        { cause: error },
      );
    }
  }
}

function blockedResult(
  fetchResult: Extract<ConsentConfigFetchResult, { readonly ok: false }>,
): Extract<ConsentInitResult, { readonly status: 'blocked' }> {
  if (fetchResult.error.code === 'sdk_incompatible') {
    return Object.freeze({
      status: 'blocked',
      reason: 'sdk_incompatible',
      retryable: false,
    });
  }

  const invalidConfigCodes: readonly ConsentConfigFetchErrorCode[] = [
    'invalid_config',
    'invalid_content_type',
    'invalid_json',
    'redirect_rejected',
    'response_too_large',
  ];
  const reason: ConsentBlockedReason = invalidConfigCodes.includes(
    fetchResult.error.code,
  )
    ? 'config_invalid'
    : 'config_unavailable';

  return Object.freeze({ status: 'blocked', reason, retryable: true });
}

export function createConsentClient(
  dependencies: ConsentClientDependencies = {},
): ConsentClient {
  let snapshot: ConsentClientSnapshot = IDLE_SNAPSHOT;
  let initOptions: ConsentInitOptions | null = null;
  let initPromise: Promise<ConsentInitResult> | null = null;
  let activeConfig: ConsentConfiguration | null = null;
  let activeState: ConsentState | null = null;
  let retainedState: ConsentState | null = null;
  let storage: ConsentCookieStorage | null = null;
  let eventDispatcher: ConsentEventDispatcher | null = null;
  let operationQueue: Promise<void> = Promise.resolve();

  const listeners = new Map<
    keyof ConsentEventMap,
    Set<(event: never) => void>
  >();
  const now = dependencies.now ?? (() => new Date());

  function reportCallbackError(error: unknown): void {
    if (!dependencies.onCallbackError) return;

    try {
      dependencies.onCallbackError(error);
    } catch {
      // Callback error reporting must never break the consent lifecycle.
    }
  }

  function emit<EventName extends keyof ConsentEventMap>(
    eventName: EventName,
    event: ConsentEventMap[EventName],
  ): void {
    const callbacks = listeners.get(eventName);
    if (!callbacks) return;

    for (const callback of [...callbacks]) {
      try {
        callback(event as never);
      } catch (error) {
        reportCallbackError(error);
      }
    }
  }

  function updateSnapshot(next: ConsentClientSnapshot): void {
    const previous = snapshot;
    snapshot = Object.freeze(next);
    emit('status-changed', { previous, current: snapshot });

    if (snapshot.status === 'ready' && previous.status !== 'ready') {
      emit('ready', { state: snapshot.state });
    } else if (snapshot.status === 'required') {
      emit('required', { reason: snapshot.reason });
    } else if (snapshot.status === 'blocked') {
      emit('blocked', {
        reason: snapshot.reason,
        retryable: snapshot.retryable,
      });
    }
  }

  async function initialize(options: ConsentInitOptions): Promise<ConsentInitResult> {
    updateSnapshot(INITIALIZING_SNAPSHOT);
    activeConfig = null;
    activeState = null;
    retainedState = null;
    storage = null;
    eventDispatcher?.stop();
    eventDispatcher = null;

    validateInitOptions(options);

    const fetchResult = await fetchConsentConfiguration({
      siteId: options.siteId,
      configUrl: options.configUrl,
      sdkVersion: VERSION,
      ...(options.configTimeoutMs === undefined
        ? {}
        : { timeoutMs: options.configTimeoutMs }),
      ...(options.allowInsecureLocalhost === undefined
        ? {}
        : { allowInsecureLocalhost: options.allowInsecureLocalhost }),
      ...(dependencies.fetch === undefined ? {} : { fetch: dependencies.fetch }),
    });

    if (!fetchResult.ok) {
      if (programmerConfigError(fetchResult.error.code)) {
        updateSnapshot(IDLE_SNAPSHOT);
        throw new ConsentClientError(
          'initialization_options_invalid',
          fetchResult.error.message,
        );
      }

      const blocked = blockedResult(fetchResult);
      updateSnapshot(blocked);
      return blocked;
    }

    activeConfig = fetchResult.config;

    try {
      storage = createConsentCookieStorage({
        consentLifetimeDays: activeConfig.consentLifetimeDays,
        ...(options.cookieName === undefined
          ? {}
          : { cookieName: options.cookieName }),
        ...(options.secureCookie === undefined
          ? {}
          : { secure: options.secureCookie }),
        ...(dependencies.document === undefined
          ? {}
          : { document: dependencies.document }),
        now,
      });
    } catch (error) {
      updateSnapshot(IDLE_SNAPSHOT);
      const isInvalidCookieName =
        error instanceof ConsentCookieStorageError &&
        error.code === 'invalid_cookie_name';
      throw new ConsentClientError(
        isInvalidCookieName
          ? 'initialization_options_invalid'
          : 'storage_unavailable',
        isInvalidCookieName
          ? 'Consent cookie name is invalid.'
          : 'Consent cookie storage could not be initialized.',
        { cause: error },
      );
    }

    let stored: ReturnType<ConsentCookieStorage['read']>;
    let requirement: ReturnType<typeof evaluateConsentRequirement>;
    try {
      stored = storage.read();
      requirement = evaluateConsentRequirement(
        activeConfig,
        stored,
        now(),
      );
    } catch (error) {
      activeConfig = null;
      storage = null;
      updateSnapshot(IDLE_SNAPSHOT);
      throw new ConsentClientError(
        'storage_unavailable',
        'Consent cookie storage could not be read safely.',
        { cause: error },
      );
    }

    if (requirement.status === 'valid') {
      setupEventDispatcher(options);
      activeState = requirement.state;
      const ready = Object.freeze({
        status: 'ready' as const,
        state: requirement.state,
        config: activeConfig,
        configSource: 'network' as const,
      });
      updateSnapshot(ready);
      return ready;
    }

    retainedState =
      stored.ok &&
      (requirement.reason === 'state_expired' ||
        requirement.reason === 'reconsent_required')
        ? stored.state
        : null;
    const required = Object.freeze({
      status: 'required' as const,
      reason: requirement.reason,
      config: activeConfig,
    });
    updateSnapshot(required);
    setupEventDispatcher(options);
    return required;
  }

  function setupEventDispatcher(options: ConsentInitOptions): void {
    if (options.eventUrl === undefined || eventDispatcher) return;

    try {
      const queue = createConsentEventQueue({
        ...(dependencies.localStorage === undefined
          ? {}
          : { storage: dependencies.localStorage }),
        now,
      });
      eventDispatcher = createConsentEventDispatcher({
        queue,
        eventUrl: options.eventUrl,
        ...(options.eventTimeoutMs === undefined
          ? {}
          : { timeoutMs: options.eventTimeoutMs }),
        ...(options.allowInsecureLocalhost === undefined
          ? {}
          : { allowInsecureLocalhost: options.allowInsecureLocalhost }),
        ...(dependencies.fetch === undefined ? {} : { fetch: dependencies.fetch }),
        ...(dependencies.onlineTarget === undefined
          ? {}
          : { onlineTarget: dependencies.onlineTarget }),
        now,
        onCompleted: (clientEventId) => {
          emit('event-sync-completed', { clientEventId });
        },
        onFailed: (clientEventId, retryable) => {
          emit('event-sync-failed', { clientEventId, retryable });
        },
        onError: reportCallbackError,
      });
      eventDispatcher.start();
    } catch (error) {
      reportCallbackError(error);
    }
  }

  function runInitialization(options: ConsentInitOptions): Promise<ConsentInitResult> {
    const promise = initialize(options)
      .catch((error: unknown) => {
        if (snapshot.status === 'initializing') {
          updateSnapshot(IDLE_SNAPSHOT);
        }
        initOptions = null;
        throw error;
      })
      .finally(() => {
        if (initPromise === promise) {
          initPromise = null;
        }
      });
    initPromise = promise;
    return promise;
  }

  function enqueueDecision(
    eventType: ConsentEventType,
    transition: (
      previous: ConsentState | null,
      context: ConsentTransitionContext,
    ) => ConsentState,
  ): Promise<ConsentState> {
    const operation = operationQueue.then(() => {
      if (
        (snapshot.status !== 'ready' && snapshot.status !== 'required') ||
        !activeConfig ||
        !storage
      ) {
        throw new ConsentClientError(
          'action_not_allowed',
          'Consent decisions are allowed only after a ready or required initialization.',
        );
      }

      const previousActiveState = activeState;
      const transitionBase = activeState ?? retainedState;
      const currentTime = now();
      if (Number.isNaN(currentTime.getTime())) {
        throw new ConsentClientError(
          'storage_write_failed',
          'Consent clock returned an invalid Date.',
        );
      }

      const consentId =
        transitionBase?.id ??
        createConsentId({
          now: () => currentTime.getTime(),
          ...(dependencies.crypto === undefined
            ? {}
            : { crypto: dependencies.crypto }),
        });
      const context: ConsentTransitionContext = {
        consentId,
        policyVersion: activeConfig.policyVersion,
        configurationVersion: activeConfig.configurationVersion,
        updatedAt: currentTime.toISOString(),
      };
      const next = transition(transitionBase, context);

      try {
        storage.write(next);
      } catch (error) {
        throw new ConsentClientError(
          'storage_write_failed',
          'Consent state could not be persisted.',
          { cause: error },
        );
      }

      activeState = next;
      retainedState = null;
      const ready = Object.freeze({
        status: 'ready' as const,
        state: next,
        config: activeConfig,
        configSource: 'network' as const,
      });
      updateSnapshot(ready);
      emit('changed', { previous: previousActiveState, current: next });
      if (eventDispatcher && initOptions) {
        try {
          eventDispatcher.enqueue(
            createConsentEvent({
              siteId: initOptions.siteId,
              state: next,
              eventType,
              ...(initOptions.locale === undefined
                ? {}
                : { locale: initOptions.locale }),
              now: () => currentTime.getTime(),
              ...(dependencies.crypto === undefined
                ? {}
                : { crypto: dependencies.crypto }),
            }),
          );
        } catch (error) {
          reportCallbackError(error);
        }
      }
      return next;
    });

    operationQueue = operation.then(
      () => undefined,
      () => undefined,
    );
    return operation;
  }

  const client: ConsentClient = {
    init(options): Promise<ConsentInitResult> {
      if (initOptions) {
        if (!sameInitOptions(initOptions, options)) {
          return Promise.reject(
            new ConsentClientError(
              'already_initialized',
              'Consent client cannot be initialized with different options.',
            ),
          );
        }

        if (initPromise) return initPromise;
        if (
          snapshot.status === 'ready' ||
          snapshot.status === 'required' ||
          snapshot.status === 'blocked'
        ) {
          return Promise.resolve(snapshot);
        }
      }

      initOptions = Object.freeze({ ...options });
      return runInitialization(initOptions);
    },

    retry(): Promise<ConsentInitResult> {
      if (initPromise) return initPromise;
      if (!initOptions || snapshot.status !== 'blocked') {
        return Promise.reject(
          new ConsentClientError(
            'retry_not_available',
            'Retry is available only after a blocked initialization.',
          ),
        );
      }
      if (!snapshot.retryable) {
        return Promise.resolve(snapshot);
      }
      return runInitialization(initOptions);
    },

    getSnapshot(): ConsentClientSnapshot {
      return snapshot;
    },

    getState(): ConsentState | null {
      return snapshot.status === 'ready' ? activeState : null;
    },

    has(category): boolean {
      if (category === 'necessary') return true;
      return snapshot.status === 'ready'
        ? (activeState?.categories[category] ?? false)
        : false;
    },

    acceptAll(): Promise<ConsentState> {
      return enqueueDecision('accept_all', transitionAcceptAll);
    },

    rejectAll(): Promise<ConsentState> {
      return enqueueDecision('reject_all', transitionRejectAll);
    },

    setPreferences(preferences: ConsentPreferenceInput): Promise<ConsentState> {
      return enqueueDecision('preferences_saved', (previous, context) =>
        transitionSavePreferences(previous, preferences, context),
      );
    },

    on<EventName extends keyof ConsentEventMap>(
      eventName: EventName,
      callback: (event: ConsentEventMap[EventName]) => void,
    ): () => void {
      let callbacks = listeners.get(eventName);
      if (!callbacks) {
        callbacks = new Set();
        listeners.set(eventName, callbacks);
      }
      const storedCallback = callback as (event: never) => void;
      callbacks.add(storedCallback);
      return () => callbacks?.delete(storedCallback);
    },
  };

  return Object.freeze(client);
}
